import * as React from "react";
import { Alert, Box, Dialog, DialogContent, type Theme, useMediaQuery } from "@mui/material";
import { DIALOG_WIDTH } from "../../constants";
import type { CloseDialogHandler } from "../../contexts/DialogContext";
import { useServiceMap } from "../../hooks/query/useServiceMap";
import { useSystemAddDependency } from "../../hooks/mutation/update/useServiceAddDependency";
import { useSystemRemoveDependency } from "../../hooks/mutation/update/useServiceRemoveDependency";
import { useSystemAddDependents } from "../../hooks/mutation/update/useServiceAddDependents";
import { useSystemRemoveDependents } from "../../hooks/mutation/update/useServiceRemoveDependents";
import { DependenciesManager } from "../DependenciesManager";
import StandardDialogActions from "./StandardDialogActions";
import StandardDialogTitle from "./StandardDialogTitle";
import { useEffect } from "react";

const unique = (list: string[]): string[] => Array.from(new Set(list));

const ManageDependenciesDialog: React.FC<{
  onDialogClosed: CloseDialogHandler;
  serviceMapId: string;
  serviceId: string;
}> = ({ onDialogClosed, serviceMapId, serviceId }) => {
  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const { data: serviceMap = { nodes: [], edges: [] } } = useServiceMap(serviceMapId);
  const [dependencies, setDependencies] = React.useState<string[]>([]);
  const [dependents, setDependents] = React.useState<string[]>([]);

  const { mutateAsync: addDependencies, isPending: isAddingDependencies } = useSystemAddDependency();
  const { mutateAsync: removeDependencies, isPending: isRemovingDependencies } = useSystemRemoveDependency();
  const { mutateAsync: addDependents, isPending: isAddingDependents } = useSystemAddDependents();
  const { mutateAsync: removeDependents, isPending: isRemovingDependents } = useSystemRemoveDependents();

  const isPending =
    isAddingDependencies ||
    isRemovingDependencies ||
    isAddingDependents ||
    isRemovingDependents;

  const initialDependencies = React.useMemo(() => {
    return unique(
      serviceMap.edges
        .filter((edge) => edge.target === serviceId)
        .map((edge) => edge.source),
    );
  }, [serviceId, serviceMap.edges]);

  const initialDependents = React.useMemo(() => {
    return unique(
      serviceMap.edges
        .filter((edge) => edge.source === serviceId)
        .map((edge) => edge.target),
    );
  }, [serviceId, serviceMap.edges]);

  useEffect(() => {
    setDependencies(initialDependencies);
  }, [initialDependencies]);

  useEffect(() => {
    setDependents(initialDependents);
  }, [initialDependents]);

  const onClose = () => {
    if (isPending) {
      return;
    }
    setSubmitError(null);
    onDialogClosed();
  };

  const onSave = async () => {
    try {
      setSubmitError(null);

      const dependenciesToAdd = dependencies.filter((id) => !initialDependencies.includes(id));
      const dependenciesToRemove = initialDependencies.filter((id) => !dependencies.includes(id));
      const dependentsToAdd = dependents.filter((id) => !initialDependents.includes(id));
      const dependentsToRemove = initialDependents.filter((id) => !dependents.includes(id));

      if (dependenciesToAdd.length > 0) {
        await addDependencies({
          serviceMapId,
          systemId: serviceId,
          dependencies: dependenciesToAdd,
        });
      }
      if (dependenciesToRemove.length > 0) {
        await removeDependencies({
          serviceMapId,
          systemId: serviceId,
          dependencies: dependenciesToRemove,
        });
      }
      if (dependentsToAdd.length > 0) {
        await addDependents({
          serviceMapId,
          systemId: serviceId,
          dependents: dependentsToAdd,
        });
      }
      if (dependentsToRemove.length > 0) {
        await removeDependents({
          serviceMapId,
          systemId: serviceId,
          dependents: dependentsToRemove,
        });
      }

      onDialogClosed({
        message: "Connections updated!",
        severity: "success",
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Could not update dependencies.";
      setSubmitError(message);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      fullScreen={isSmallScreen}
      PaperProps={{ sx: { maxWidth: isSmallScreen ? undefined : DIALOG_WIDTH.lg } }}
    >
      <StandardDialogTitle
        title="Manage"
        isSmallScreen={isSmallScreen}
        onClose={onClose}
      />

      <DialogContent sx={{ overflowX: "hidden" }}>
        <Box display="flex" flexDirection="column" gap={2}>
          <DependenciesManager
            serviceMap={serviceMap}
            serviceId={serviceId}
            dependencies={dependencies}
            dependents={dependents}
            onDependenciesChange={setDependencies}
            onDependentsChange={setDependents}
          />
          {submitError && <Alert severity="error">{submitError}</Alert>}
        </Box>
      </DialogContent>

      <StandardDialogActions
        primaryText="Save"
        primaryIcon={<></>}
        onCancel={onClose}
        onPrimaryClicked={() => {
          void onSave();
        }}
        loading={isPending}
        disabled={isPending}
      />
    </Dialog>
  );
};

export default ManageDependenciesDialog;
